
import { useTranslation } from 'react-i18next';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

const Hero = () => {
  const { t } = useTranslation();
  const [displayedText, setDisplayedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const fullText = t('hero.headline');
  
  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setDisplayedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
        setShowCursor(false);
      }
    }, 30);
    
    return () => clearInterval(interval);
  }, [fullText]);
  
  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-background">
      {/* Background minimalista */}
      <div className="absolute inset-0">
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute top-20 right-20 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px]"
        />
      </div>
      
      {/* Content */}
      <div className="container-custom relative z-10 pt-32 pb-20">
        <div className="max-w-6xl mx-auto">
          {/* Headline com efeito de digitação */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-[1.1] tracking-tight text-white max-w-4xl min-h-[180px] md:min-h-[220px]">
            {displayedText}
            {showCursor && displayedText.length > 0 && (
              <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.8, repeat: Infinity }}
                className="inline-block w-1 h-16 md:h-20 bg-primary ml-1 align-middle"
              />
            )}
          </h1>
          
          {/* Subheadline - smooth bottom to top */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2, ease: "easeOut" }}
            className="text-xl md:text-2xl text-gray-400 mb-12 leading-relaxed max-w-3xl"
          >
            {t('hero.subheadline')}
          </motion.p>
          
          {/* CTAs - smooth bottom to top */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2.5, ease: "easeOut" }}
            className="flex flex-wrap items-center gap-4 mb-24"
          >
            <motion.button
              onClick={() => scrollToSection('#contact')}
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0, 155, 58, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-primary hover:bg-primary-light text-background font-semibold rounded-xl transition-all flex items-center gap-2 shadow-lg shadow-primary/20"
            >
              {t('hero.cta_primary')}
              <ArrowRight className="w-5 h-5" />
            </motion.button>
            
            <motion.button
              onClick={() => scrollToSection('#projects')}
              whileHover={{ scale: 1.05, backgroundColor: "rgba(255, 255, 255, 0.15)" }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-white/10 text-white font-semibold rounded-xl transition-all border border-white/20 hover:border-white/30"
            >
              {t('hero.cta_secondary')}
            </motion.button>
          </motion.div>
          
          {/* Scroll Indicator - smooth bottom to top */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 3, ease: "easeOut" }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2"
          >
            <button
              onClick={() => scrollToSection('#services')}
              className="flex flex-col items-center gap-2 text-gray-600 hover:text-gray-400 transition-colors"
            >
              <span className="text-xs">{t('hero.scroll_hint')}</span>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ChevronDown className="w-5 h-5" />
              </motion.div>
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
